import {
  ArrowDownFill24Icon,
  ArrowUpFill24Icon,
  ArrowUpOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { format } from "date-fns";
import { OploggElement } from "features/TodoOploggSidebar/Oplogg/OploggElement/OploggElement";
import { useOplogg } from "features/TodoOploggSidebar/Oplogg/useOplogg";
import { commonPadding } from "features/TodoOploggSidebar/formSchema";
import {
  ReactElement,
  ReactNode,
  UIEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DropsButton,
  HStack,
  IconButton,
  Text,
  tokens,
} from "shared/components/Spor";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import styled from "styled-components";

export const OploggMessageWrapper = styled.div`
  grid-area: messages;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding-left: 12px;
  padding-right: 12px;
`;

export const OploggList = styled.ul<{ $readonly: boolean }>`
  display: flex;
  flex-direction: column;
  overflow-y: ${({ $readonly }) => ($readonly ? "hidden" : "scroll")};
  height: 100%;
  width: 100%;
  margin: 0;
  list-style: none;
  padding: 12px 0px 0px 0px;
  scroll-behavior: smooth;
  position: relative;
  min-height: 0;
  height: 100%;
  scrollbar-color: ${({ theme }) => theme.colorSelected} rgba(0, 0, 0, 0);
  -webkit-scrollbar: none; /* WebKit */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &:has(li[data-show-new-indicator="true"]) .indicators .moremsgs {
    display: flex !important;
  }

  &:has(li[data-show-bookmarked-indicator="true"]) .indicators .bookmarked {
    display: flex !important;
  }
`;

interface InidicatorProps {
  placement: "top" | "bottom";
}

const Indicators = styled.div<InidicatorProps>`
  display: flex;
  gap: 6px;
  justify-content: center;
  position: sticky;
  ${({ placement }) =>
    placement === "top" ? "top: 6px; order: -1;" : "bottom: 6px;"}
`;

const NewIndicatorButton = styled(DropsButton)`
  display: none !important;
  background-color: ${({ theme }) => theme.colorAlarm} !important;
  color: ${({ theme }) => theme.colorAlarmTextTertiary} !important;
  align-self: flex-start;

  &:hover {
    background-color: ${({ theme }) => theme.colorAlarmPulse} !important;
  }
`;

const NewIndicator = ({
  children,
  onClick,
}: {
  children: ReactNode[];
  onClick: () => void;
}) => (
  <NewIndicatorButton
    className="moremsgs"
    size="md"
    leftIcon={<ArrowUpOutline24Icon />}
    aria-label="Gå til nye uleste meldinger"
    onClick={onClick}
  >
    {...children}
  </NewIndicatorButton>
);

const BookmarkIndicatorButton = styled(IconButton)`
  display: none !important;
  align-self: flex-end;
`;

const BookmarkIndicator = ({
  icon,
  onClick,
}: {
  icon: ReactElement;
  onClick: () => void;
}) => (
  <BookmarkIndicatorButton
    className="bookmarked"
    aria-label="Gå til bokmerket melding"
    variant="floating"
    icon={icon}
    onClick={onClick}
  />
);

export const BorderHr = styled.hr`
  color: ${({ theme }) => theme.colorOutlineMain};
  margin-left: -${tokens.default.size.spacing[commonPadding]};
  margin-right: -${tokens.default.size.spacing[commonPadding]};
`;

const debounce = (
  callback: (...args: unknown[]) => void,
  timeoutMs: number,
) => {
  let timeoutId: number | undefined;

  return (...args: unknown[]) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback(...args);
    }, timeoutMs);
  };
};

const OploggHeader = () => {
  const [, setUpdateClock] = useState(false);

  useEffect(() => {
    const timerID = setInterval(() => setUpdateClock((value) => !value), 1000);

    return function cleanup() {
      clearInterval(timerID);
    };
  });

  return (
    <HStack p="12px" justifyContent="space-between">
      <Text variant="md" style={{ fontFamily: "Vy Display" }}>
        {format(new Date(), "HH")}:{format(new Date(), "mm")}
      </Text>
      <Text variant="md" style={{ fontFamily: "Vy Display" }}>
        Operativ logg
      </Text>
    </HStack>
  );
};

type OploggMessagesProps = {
  showHeader?: boolean;
  readonly?: boolean;
};

const OploggMessages = ({
  showHeader = false,
  readonly = false,
}: OploggMessagesProps) => {
  const { data, status, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useOplogg();

  const containerRef = useRef<HTMLUListElement | null>(null);
  const [indicatorsState, setIndicatorsState] = useState<{
    moreMsgsCounter: number;
    highlightedEl: Element | null;
  }>({
    moreMsgsCounter: 0,
    highlightedEl: null,
  });

  const [highlightedLogId, setHighlightedLogId] = useState<string>("");
  const onHighlightItem = (id: string) => {
    setHighlightedLogId(id === highlightedLogId ? "" : id);
  };

  const [bookmarkIsAbove, setBookmarkIsAbove] = useState(false);

  const checkBookmarkIsAbove: UIEventHandler<HTMLUListElement> = () => {
    const indicatorElement = document.querySelector("[data-bookmarked]");
    const refElement = containerRef.current;

    if (indicatorElement && refElement) {
      const indicatorRect = indicatorElement.getBoundingClientRect();
      const refRect = refElement.getBoundingClientRect();

      const indicatorMiddle = (indicatorRect.top + indicatorRect.bottom) / 2;
      const refMiddle = (refRect.top + refRect.bottom) / 2;

      setBookmarkIsAbove(indicatorMiddle < refMiddle);
    }
  };

  const getHighlightedEl: UIEventHandler<HTMLUListElement> = debounce(() => {
    const elements = Array.from(containerRef.current?.children ?? []);

    const highlightedEl = elements.find((el) =>
      el.getAttribute("data-show-bookmarked-indicator"),
    );

    setIndicatorsState((prevState) => ({
      ...prevState,
      highlightedEl: highlightedEl ?? null,
    }));
  }, 500);

  const onScroll: UIEventHandler<HTMLUListElement> = (event) => {
    checkBookmarkIsAbove(event);
    getHighlightedEl(event);
  };

  const goToTop = () => containerRef.current?.scrollTo(0, 0);

  const goToBookmark = () => {
    indicatorsState.highlightedEl?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "center",
    });
  };

  const allEntries = useMemo(
    () =>
      data?.pages
        .flatMap((group) => group.entries)
        .sort((a, b) => (b.cimId ?? 0) - (a.cimId ?? 0))
        .sort(
          (a, b) =>
            // Always present (same as createdAt until registered in CIM)
            // but only has minute resolution, so we sort on cimId first to be
            // sure that the message that was registered latest appears at the top
            new Date(b.reportedTime ?? new Date()).valueOf() -
            new Date(a.reportedTime ?? new Date()).valueOf(),
        ),
    [data],
  );

  useEffect(() => {
    const elements = Array.from(containerRef.current?.children ?? []);

    const showNewEls = elements.filter(
      (el) => el.getAttribute("data-show-new-indicator") === "true",
    );

    const msgCounter = showNewEls.length;

    setIndicatorsState((prevState) => ({
      ...prevState,
      moreMsgsCounter: msgCounter,
    }));
  }, [containerRef.current, data]);

  return (
    <OploggMessageWrapper>
      {showHeader && <OploggHeader />}

      {status === "pending" && <SkeletonLoader skeletonType="oplogg" />}

      {status === "error" && <FailureMessage />}

      {status === "success" && allEntries && allEntries.length > 0 ? (
        <OploggList ref={containerRef} onScroll={onScroll} $readonly={readonly}>
          {allEntries.map((entry) => (
            <OploggElement
              key={entry.uuid}
              logEntry={entry}
              isHighlighted={entry.uuid === highlightedLogId}
              onClick={() => onHighlightItem(entry.uuid)}
              readonly={readonly}
            />
          ))}

          {!readonly && (
            <>
              {hasNextPage && (
                <DropsButton
                  variant="secondary"
                  size="sm"
                  loading={isFetchingNextPage}
                  style={{
                    margin: "18px auto",
                  }}
                  onClick={() => fetchNextPage()}
                >
                  Hent flere
                </DropsButton>
              )}

              <Indicators className="indicators" placement="top">
                <NewIndicator onClick={goToTop}>
                  {indicatorsState.moreMsgsCounter}
                  &nbsp;
                  {indicatorsState.moreMsgsCounter > 1
                    ? "nye meldinger"
                    : "ny melding"}
                </NewIndicator>

                {bookmarkIsAbove && (
                  <BookmarkIndicator
                    icon={<ArrowUpFill24Icon />}
                    onClick={goToBookmark}
                  />
                )}
              </Indicators>

              <Indicators className="indicators" placement="bottom">
                {!bookmarkIsAbove && (
                  <BookmarkIndicator
                    icon={<ArrowDownFill24Icon />}
                    onClick={goToBookmark}
                  />
                )}
              </Indicators>
            </>
          )}
        </OploggList>
      ) : (
        <NoDataMessage reason="Fant ingen oplogg-meldinger" />
      )}
    </OploggMessageWrapper>
  );
};

export default withErrorBoundary(OploggMessages);
